import cloneDeep from 'lodash/cloneDeep'
import isEqual from 'lodash/isEqual'
import keyBy from 'lodash/keyBy'

import api from '../api'
import * as mutationTypes from './mutation-types'
import filterTypes from './../filter-types'

import { v4 as generateUuid } from 'uuid'
import readableColumnName from '../utils/readable-column-name'
import { filterTitle } from '../utils/filter-texts'
import sortBy from 'lodash/sortBy'

const getters = {
  getActiveFilterByUuid: (state) => (uuid) => {
    return state.activeFilters.find((filter) => filter.uuid === uuid)
  },

  getActiveFilters: (state) => {
    return sortBy(
      state.activeFilters,
      (f) => state.activeFilterPositions[f.uuid] || 0
    )
  },

  getFilterHeaderTitle: (_state, getters) => (filter) => {
    const { columnName } = filter

    // Rest are defaults before migrating to segment filter column names
    const filterType = getters.getFilterByType(filter.type)

    const customFilterTitle =
      filterType.getColumnTitle &&
      filterType.getColumnTitle(filter.settings, getters)

    const finalColumnName = readableColumnName(
      columnName,
      filterType.name_key,
      customFilterTitle
    )

    if (finalColumnName !== '') {
      return finalColumnName
    }

    return filterTitle(filterType.name_key)
  },

  getFilterByType: (state) => (type) => {
    return state.allFilters.find((filter) => filter.type === type)
  },

  getFilterDropdownValueLabel: (state) => (type, value) => {
    const filterValueList = state.filterDropdownValues[`${type}_list`]

    // When there is no dropdown value for filterType
    if (!filterValueList) return value
    // When fetchFilterDropdownValues API call is not finished
    else if (filterValueList.length === 0) return ''

    return filterValueList.find((item) => item.value === value)?.label
  },

  isActiveFiltersModified: (state) => {
    return (
      !isEqual(state.activeFilters, state.originalActiveFilters) ||
      state.activeFilterPositionsChanged
    )
  },

  anyLedgerSpecificFiltersActive: (state) => {
    return state.activeFilters.some(
      (filter) => state.allFiltersByType[filter.type].ledgerRequired
    )
  },

  hasLedgerSpecificFiltersWithoutPeriodSelection: (
    state,
    getters,
    rootState
  ) => {
    return !rootState.periodSelection && getters.anyLedgerSpecificFiltersActive
  },

  activeFilterSubtitle: (state, getters) => {
    return (filter) => {
      return state.allFiltersByType[filter.type].activeSubtitle(
        filter.settings,
        getters
      )
    }
  },

  filterSettingsProperties: (state) => {
    return {
      filter: state.editingFilter,
    }
  },
}

const mutations = {
  [mutationTypes.ADD_ACTIVE_FILTER](state, filter) {
    state.activeFilters.push(filter)
  },

  [mutationTypes.UPDATE_ACTIVE_FILTER](
    state,
    { filter, settings, columnName }
  ) {
    filter.settings = settings
    filter.columnName = columnName
  },

  [mutationTypes.REMOVE_ACTIVE_FILTER](state, removeFilter) {
    const filterIndex = state.activeFilters.findIndex(
      (filter) => filter.uuid === removeFilter.uuid
    )

    if (filterIndex !== -1) {
      state.activeFilters.splice(filterIndex, 1)
    }
  },

  [mutationTypes.SET_ACTIVE_FILTERS](state, newFilters) {
    state.activeFilters = newFilters.map((filter) => ({
      uuid: filter.cached_uuid || generateUuid(),
      ...filter,
    }))
    state.originalActiveFilters = cloneDeep(state.activeFilters)

    state.activeFilters.forEach(
      (filter, index) => (state.activeFilterPositions[filter.uuid] = index)
    )

    state.activeFilterPositionsChanged = false
  },
  [mutationTypes.UNSET_ACTIVE_FILTERS](state) {
    state.activeFilters = []
    state.originalActiveFilters = []
  },

  [mutationTypes.SET_EDITING_FILTER](state, filter) {
    state.editingFilter = { ...filter }
  },

  [mutationTypes.SET_FILTER_DROPDOWN_VALUES_LOADING](state, value) {
    state.filterDropdownValuesLoading = value
  },

  [mutationTypes.SET_FILTER_DROPDOWN_VALUES](state, filterDropdownValues) {
    state.filterDropdownValues = {
      company_template_list: filterDropdownValues.company_templates.map(
        (f) => ({
          value: f.id,
          label: f.name,
        })
      ),
      company_type_list: filterDropdownValues.company_types.map((f) => ({
        value: f,
        label: I18n.t(`company.company_types.${f}`),
      })),
      country_list: filterDropdownValues.countries.map((f) => ({
        value: f.code,
        label: f.name,
      })),
      currency_list: filterDropdownValues.currencies.map((f) => ({
        value: f,
        label: f,
      })),
      reporting_frequency_list: filterDropdownValues.report_frequencies.map(
        (f) => ({ value: f, label: I18n.t(`company.${f}_ledgers_per_year`) })
      ),
      firm_workflow_list: filterDropdownValues.firm_workflows.map((f) => ({
        value: f.id,
        label: f.name,
      })),
      workflow_status_list: filterDropdownValues.workflow_statuses.map((f) => ({
        value: f.id,
        label: f.name,
      })),
      account_mapping_list_list: filterDropdownValues.account_mapping_lists.map(
        (f) => ({
          value: f.id,
          label: f.name,
        })
      ),
      sync_entity_list: [
        { label: I18n.t('companies.sync_entity_info.manual'), value: -1 },
        ...filterDropdownValues.sync_entities.map((f) => ({
          value: f.id,
          label: f.name,
        })),
      ],
      groups_list: filterDropdownValues.groups.map((group) => ({
        value: group.id,
        label: group.name,
      })),
    }
  },

  [mutationTypes.UPDATE_ACTIVE_FILTER_POSITION](state, { uuid, position }) {
    state.activeFilterPositions[uuid] = position
    state.activeFilterPositionsChanged = true
  },
  [mutationTypes.RESET_ACTIVE_FILTER_POSITIONS](state) {
    state.activeFilterPositions = {}
    state.activeFilters.forEach(
      (filter, index) => (state.activeFilterPositions[filter.uuid] = index)
    )
  },
}

const actions = {
  async fetchFilterDropdownValues({ commit }) {
    commit(mutationTypes.SET_FILTER_DROPDOWN_VALUES_LOADING, true)
    const filterDropdownValues = await api.fetchFilterDropdownValues()

    commit(mutationTypes.SET_FILTER_DROPDOWN_VALUES, filterDropdownValues)
    commit(mutationTypes.SET_FILTER_DROPDOWN_VALUES_LOADING, false)
  },

  editFilter({ commit }, filter) {
    commit(mutationTypes.SET_EDITING_FILTER, filter)
  },

  resetFilter({ commit }) {
    commit(mutationTypes.SET_EDITING_FILTER, null)
  },

  saveFilter(
    { dispatch, getters },
    { uuid, filterType, settings, columnName }
  ) {
    const normalizeSettings =
      getters.getFilterByType(filterType).normalizeSettings

    const normalizedSettings = normalizeSettings
      ? normalizeSettings(settings)
      : settings

    dispatch('addOrUpdateFilter', {
      uuid,
      filterType,
      columnName,
      settings: normalizedSettings,
    })
  },

  setActiveFilters({ commit }, filters) {
    commit(mutationTypes.SET_ACTIVE_FILTERS, filters)
    commit(mutationTypes.RESET_ACTIVE_FILTER_POSITIONS)
  },

  unsetActiveFilters({ commit }) {
    commit(mutationTypes.UNSET_ACTIVE_FILTERS)
    commit(mutationTypes.RESET_ACTIVE_FILTER_POSITIONS)
  },

  removeActiveFilter({ commit }, filter) {
    commit(mutationTypes.REMOVE_ACTIVE_FILTER, filter)
    commit(mutationTypes.RESET_ACTIVE_FILTER_POSITIONS)
  },

  addOrUpdateFilter(
    { commit, getters },
    { uuid, filterType, settings, columnName }
  ) {
    const existingFilter = uuid && getters.getActiveFilterByUuid(uuid)

    if (existingFilter) {
      commit(mutationTypes.UPDATE_ACTIVE_FILTER, {
        filter: existingFilter,
        settings: settings,
        columnName: columnName,
      })
    } else {
      commit(mutationTypes.ADD_ACTIVE_FILTER, {
        uuid: uuid || generateUuid(),
        type: filterType,
        settings: settings,
        columnName: columnName,
      })
      commit(mutationTypes.RESET_ACTIVE_FILTER_POSITIONS)
    }
  },

  updateFilterPosition({ commit }, { uuid, position }) {
    commit(mutationTypes.UPDATE_ACTIVE_FILTER_POSITION, { uuid, position })
  },
}

export default {
  state() {
    return {
      filters: [],
      activeFilters: [],
      originalActiveFilters: [],
      activeFilterPositions: {},
      activeFilterPositionsChanged: false,
      allFilters: filterTypes,
      allFiltersByType: keyBy(filterTypes, 'type'),
      editingFilter: null,
      filterDropdownValuesLoading: false,
      filterDropdownValues: {
        company_template_list: [],
        company_type_list: [],
        country_list: [],
        currency_list: [],
        reporting_frequency_list: [],
        sync_entity_list: [],
        firm_workflow_list: [],
        workflow_statuses: [],
      },
    }
  },
  getters,
  mutations,
  actions,
}
